import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "./useAppContext";
import { useSmartFilterLoader } from "./useSmartFilterLoader";
import { RefObject } from "use-callback-ref/dist/es5/types";

/** A function that renders a spread to a div by adding a canvas and svg layer */
export function useSpreadRenderer({
	spread,
	imageSize,
	containerRef,
	canvasPreviewModeEnabled = true,
	svgPreviewModeEnabled = true,
}: {
	spread: eXpress.core.Spread | undefined;
	imageSize: eXpress.math.Vector;
	containerRef: RefObject<HTMLDivElement>;
	canvasPreviewModeEnabled?: boolean;
	svgPreviewModeEnabled?: boolean;
}) {
	const { logger, client, fontFamilyLoader, editor } = useAppContext();
	const { fallbackFontFamily } = editor;
	const smartFilterLoader = useSmartFilterLoader();

	const spreadResources = useMemo(() => {
		return new eXpress.creator.rendering.SpreadResources(smartFilterLoader, client, {
			logger,
		});
	}, [client, logger, smartFilterLoader]);

	// The Image layer (canvas)
	const layersRef = useRef<
		| {
				canvasLayer: eXpress.creator.rendering.CanvasGraphicsLayer;
				svgLayer: eXpress.creator.rendering.SvgTextLayer;
		  }
		| undefined
	>();

	useEffect(() => {
		if (typeof window === "undefined") return undefined;
		if (!spread) {
			return undefined;
		}

		const { canvas: canvasPlatform } = eXpress.platform;
		const imagePlatform = eXpress.platform;

		const dipToCssScale = eXpress.math.vector(imageSize).div(spread);

		// We currently create a new EffectCache for each spread.
		// Reason: We don't have logic yet to cleanup unused effects in a shared EffectCache

		const effectCache =
			new eXpress.creator.rendering.EffectCache<eXpress.creator.rendering.MipMapCache>(
				(image, key) =>
					new eXpress.creator.rendering.MipMapCache(
						image,
						eXpress.platform.canvas,
						logger.error.bind(logger),
						key,
					),
			);

		const canvasLayer = new eXpress.creator.rendering.CanvasGraphicsLayer(
			{
				spread,
				canRender: () => true,
				dipToCssScale,
				cssToDipScale: eXpress.math.Vector.ones.div(dipToCssScale),
				isInPreviewMode: canvasPreviewModeEnabled,
				editPageZone: null,
			},
			fontFamilyLoader,
			"",
			() => fallbackFontFamily,
			logger,
			effectCache,
			client,
			undefined, // smartFilterLoader
			imageSize,
			canvasPlatform,
			imagePlatform.image,
			{
				filter: eXpress.creator.rendering.ZoneFilter.Img,
			},
		);

		// The text layer (svg)
		const svgLayer = new eXpress.creator.rendering.SvgTextLayer(
			{
				spread,
				canRender: () => true,
				dipToCssScale,
				cssToDipScale: eXpress.math.Vector.ones.div(dipToCssScale),
				isInPreviewMode: svgPreviewModeEnabled,
				editPageZone: null,
			},
			logger,
			() => fallbackFontFamily,
			fontFamilyLoader,
			{
				renderEmptyTextZones: true,
			},
		);

		layersRef.current = {
			canvasLayer,
			svgLayer,
		};

		const containerEl = containerRef.current;
		containerEl?.appendChild(canvasLayer.element);
		containerEl?.appendChild(svgLayer.element);

		return () => {
			// Cleanup
			[...(containerEl?.childNodes.values() || [])].forEach((node) => node.remove());
			canvasLayer.dispose();
			effectCache.dispose();
			layersRef.current = undefined;
		};
	}, [
		spread,
		imageSize,
		fontFamilyLoader,
		logger,
		client,
		fallbackFontFamily,
		containerRef,
		canvasPreviewModeEnabled,
		svgPreviewModeEnabled,
	]);

	// Load render resources
	return useQuery(
		["useSpreadPreview", spread?.oid], // At the moment it will not retrigger when a spread is changed
		async () => {
			const { canvasLayer, svgLayer } = layersRef.current || {};

			if (!spread || !canvasLayer || !svgLayer) {
				return null;
			}

			const { requiredResourcesPromise, optionalResourcesPromise } =
				spreadResources.loadResourcesAsync(spread, [canvasLayer, svgLayer]);

			await Promise.all([requiredResourcesPromise, optionalResourcesPromise]);

			canvasLayer.resize(imageSize);
			canvasLayer.draw(spread.zones.filter((zone) => !eXpress.core.isAnyTextZone(zone)));

			svgLayer.resize(imageSize);
			svgLayer.draw(spread.textZones);

			return null;
		},
		{
			cacheTime: 0, // Disable cache, otherwise the code is not executed and layers not drawn (layers already have caching)
		},
	);
}
